import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useRef} from 'react';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {GetAnalysisTemplate} from 'analysis-tool/templates/graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from 'analysis-tool/templates/graphql/GetAnalysisTemplate';
import {TagSchema} from 'constants/RegistryConstants';
import {SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import FieldTable, {ClientSchemaField} from 'registry/field/FieldTable';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import FieldTableRow, {DisablableControl} from 'registry/field/FieldTableRow';
import {AssaySchemaFieldTableRowProps} from 'results/assay-schemas/AssaySchemaFieldTableRow';
import {PlateSchema, ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  generateRunFieldsSpecFromAnalysisTemplateVariables,
  type AnalysisTemplateVariableSchemaFieldSpec,
} from './AnalysisTemplateIntegrationUtils';

/**
 * Minimal schema interface data needed for requiredLink building
 */
export interface SchemaInterfaceLookup {
  id: string;
  name: string;
}

/**
 * Schema lookup data for building proper requiredLink from schema IDs.
 * tagSchemas is used both for building requiredLink (full data) and for FieldTable validation (just IDs).
 */
export interface SchemaLookupData {
  tagSchemas: Array<TagSchema>;
  plateSchemas: Array<PlateSchema>;
  containerSchemas: Array<ContainerSchema>;
  schemaInterfaces: Array<SchemaInterfaceLookup>;
  registryId?: string;
}

interface AnalysisTemplateVariableFieldTableProps {
  templateFields: Array<SchemaField | ClientSchemaField>;
  ownerId: string;
  ownerUrlId: string;
  registryId?: string;
  onFieldsChange?: (fields: Array<ClientSchemaField>) => void;
  isPreview?: boolean;
}

interface QueryingAnalysisTemplateVariableFieldTableProps {
  analysisTemplateId: string | null;
  ownerId: string;
  ownerUrlId: string;
  registryId?: string;
  onDraftFieldsChange: (fields: Array<ClientSchemaField>) => void;
  onAnalysisTemplateVariableMappingChange?: (mapping: Record<string, string>) => void;
  onError?: (error: string | null) => void;
  setIsLoading?: (isLoading: boolean) => void;
  showDescription?: boolean; // Whether to show the description text above the table
  key?: string; // Add key prop to force remount when needed
  schemaLookupData: SchemaLookupData | null; // For building proper requiredLink from schema IDs
}

/**
 * Builds a proper requiredLink from schema ID and field type.
 * For entity types (bioentity_link), looks up the tag schema.
 * For storable types (storable_link), looks up the plate schema.
 */
function buildRequiredLinkFromSchemaId(
  schemaId: string | null,
  schemaInterfaceId: string | null,
  fieldType: FieldType,
  folderItemType: string | null,
  schemaLookupData: SchemaLookupData | null
): ClientSchemaField['requiredLink'] {
  // If no schema filter and no folder item type, return null
  if (schemaId == null && schemaInterfaceId == null && folderItemType == null) {
    return null;
  }

  // For storable link fields (plates, boxes, containers)
  if (fieldType === FieldType.STORABLE_LINK) {
    const schemas = [
      ...(schemaLookupData?.plateSchemas ?? []),
      ...(schemaLookupData?.containerSchemas ?? []),
    ];
    const storableSchema = schemas.find((s) => s.id === schemaId);
    if (storableSchema) {
      return {
        storableSchema: {
          id: storableSchema.id,
          name: storableSchema.name,
          prefix: storableSchema.prefix,
          registryId: storableSchema.registryId,
          iconId: storableSchema.iconId,
          sqlIdentifier: storableSchema.sqlIdentifier,
        },
      };
    }
    return null;
  }

  // For entity link fields (bioentity_link)
  if (fieldType === FieldType.BIOENTITY_LINK) {
    if (schemaId != null && schemaLookupData?.tagSchemas) {
      const tagSchema = schemaLookupData.tagSchemas.find((s) => s.id === schemaId);
      if (tagSchema) {
        return {
          tagSchema: {
            id: tagSchema.id,
            name: tagSchema.name,
            iconId: tagSchema.iconId,
            folderItemType: tagSchema.folderItemType,
            sequenceType: tagSchema.sequenceType,
            shouldCreateAsOligo: tagSchema.shouldCreateAsOligo,
            sqlIdentifier: tagSchema.sqlIdentifier,
            archiveRecord: tagSchema.archiveRecord ?? null,
          },
          entitySchemaFieldPath: null,
          folderItemType: null,
          schemaInterface: null,
        };
      }
    }

    if (schemaInterfaceId != null && schemaLookupData?.schemaInterfaces) {
      const schemaInterface = schemaLookupData.schemaInterfaces.find((s) => s.id === schemaInterfaceId);
      if (schemaInterface) {
        return {
          tagSchema: null,
          entitySchemaFieldPath: null,
          folderItemType: null,
          schemaInterface: {
            id: schemaInterface.id,
            name: schemaInterface.name,
          },
        };
      }
    }

    if (folderItemType != null) {
      return {
        tagSchema: null,
        entitySchemaFieldPath: null,
        folderItemType,
        schemaInterface: null,
      };
    }

    return null;
  }

  // For other field types, just return with folder item type if present
  if (folderItemType != null) {
    return {
      tagSchema: null,
      entitySchemaFieldPath: null,
      folderItemType,
      schemaInterface: null,
    };
  }

  return null;
}

/**
 * Helper function to create analysis template field table rows with configurable disabled controls
 */
function createAnalysisTemplateFieldTableRow(
  props: AssaySchemaFieldTableRowProps,
  disableEditingControls = false
): JSX.Element {
  const disabledMessage =
    "This field's property is derived from an automation template, so it cannot be modified";
  const temporarilyDisabledMessage = 'This field can be edited after the automation template is saved';
  // Get field options for the field definition dropdown
  const availableFieldOptions = getFieldOptions({
    initialField: props.initialField,
    schemaFieldSelectors: props.schemaFieldSelectors,
    storableSchemas: props.storableSchemas,
    assaySchemas: props.assaySchemas,
    tagSchemas: props.tagSchemas,
    schemaInterfaces: props.schemaInterfaces,
    includeJSONField: true,
    includeAssayLinks: true,
    includeBatchLinks: true,
    includeGenericBioentityLinks: true,
    includeBooleanField: true,
    includeArchivedSchemas: false,
    includeSnapshotField: false,
  });

  const baseDisabledControls = {
    [DisablableControl.DISPLAY_NAME_INPUT]: disabledMessage,
    [DisablableControl.ARCHIVE_BUTTON]: disabledMessage,
    [DisablableControl.DELETE_BUTTON]: disabledMessage,
    [DisablableControl.IS_MULTI_CHECKBOX]: disabledMessage,
    [DisablableControl.IS_REQUIRED_CHECKBOX]: disabledMessage,
    [DisablableControl.FIELD_DEFINITION_DROPDOWN]: disabledMessage,
  };

  const editingDisabledControls = disableEditingControls
    ? {
        [DisablableControl.WAREHOUSE_NAME_INPUT]: temporarilyDisabledMessage,
      }
    : {};

  return (
    <FieldTableRow
      {...props}
      availableFieldOptions={availableFieldOptions}
      disableControlsWithTooltips={{
        ...baseDisabledControls,
        ...editingDisabledControls,
      }}
      isInEditMode={false}
      areRemoveAndArchiveEnabled={false}
      disabled={disableEditingControls}
      isRemoveOnly={false}
      shouldShowRelaxationWarning={false}
      shouldShowWarehouseWarning={false}
      isFieldMigrationAvailable={false}
      shouldShowTooltipText={true}
      schemaId={props.schema?.id ?? null}
    />
  );
}

/**
 * Custom field table row for analysis template fields that disables specific controls
 * while allowing display name and warehouse name (system name) editing after the schema is saved
 */
function AnalysisTemplateFieldTableRow(props: AssaySchemaFieldTableRowProps): JSX.Element {
  return createAnalysisTemplateFieldTableRow(props, false);
}

/**
 * Preview version of the analysis template field table row that disables all controls
 * including display name and field definition editing
 */
function PreviewAnalysisTemplateFieldTableRow(props: AssaySchemaFieldTableRowProps): JSX.Element {
  return createAnalysisTemplateFieldTableRow(props, true);
}

/**
 * Converts generated field specs from analysis template variables to ClientSchemaField format
 * and builds the mapping from field system names to analysis variable IDs.
 *
 * When schemaLookupData is non-null, schema filters are applied by building proper requiredLink
 * objects with the resolved schema details.
 */
function convertFieldSpecsToClientFields(
  fieldSpecs: Array<AnalysisTemplateVariableSchemaFieldSpec>,
  schemaLookupData: SchemaLookupData | null
): {
  fields: Array<ClientSchemaField>;
  analysisVariableMapping: Record<string, string>;
} {
  const fields = fieldSpecs.map((spec, index) => {
    const fieldName = spec.name ?? `Automation template field ${index + 1}`;

    const requiredLink = buildRequiredLinkFromSchemaId(
      spec.schemaId,
      spec.schemaInterfaceId,
      spec.fieldType,
      spec.folderItemType,
      schemaLookupData
    );

    return {
      cid: `analysis-template-field-${index}`,
      name: fieldName,
      displayName: fieldName,
      systemName: spec.systemName,
      fieldType: spec.fieldType,
      isRequired: spec.isRequired,
      isComputed: false,
      isConvertedFromLink: false,
      isSnapshot: false,
      isIntegration: false,
      isMulti: spec.isMulti ?? false,
      isParentLink: false,
      description: null,
      tooltipText: null,
      archiveRecord: null,
      requiredLink,
    };
  });

  // Build the mapping: {schema_field_system_name: pipeline_variable_id}
  const analysisVariableMapping: Record<string, string> = {};
  fieldSpecs.forEach((spec) => {
    analysisVariableMapping[spec.systemName] = spec.variableId;
  });

  return {fields, analysisVariableMapping};
}

/**
 * Core component that displays fields generated from analysis template variables
 */
export function AnalysisTemplateVariableFieldTable({
  templateFields,
  ownerId,
  ownerUrlId,
  registryId,
  onFieldsChange,
  isPreview,
}: AnalysisTemplateVariableFieldTableProps): JSX.Element {
  return (
    <div>
      <FieldTable
        key={templateFields.length === 0 ? 'empty' : templateFields[0].systemName}
        schemaId={null}
        schema={null}
        schemaHasData={false}
        isBeingCreated={false}
        isDisabled={true}
        itemClass={isPreview ? PreviewAnalysisTemplateFieldTableRow : AnalysisTemplateFieldTableRow}
        fields={templateFields as Array<ClientSchemaField>}
        onFieldsChange={onFieldsChange ?? noop}
        schemaType="automation template"
        batchSchemas={[]}
        storableSchemas={[]}
        assaySchemas={[]}
        savingState={SavingState.COMPLETED}
        ownerUrlId={ownerUrlId}
        ownerId={ownerId}
        registryId={registryId}
        shouldShowIsRequired={true}
        shouldShowIsIntegration={false}
        shouldShowIsParentLink={false}
        shouldShowMultiSelect={true}
        shouldShowTooltipText={false}
        dataTestAttrs={{component: 'AnalysisTemplateVariableFieldTable', element: 'fieldTable'}}
        enforceUniqueDisplayName={false}
        title="Automation template fields"
        titleTooltip="Fields automatically generated from the selected analysis template variables. System names can be modified."
        shouldReplaceFieldsFromProps={true}
      />
    </div>
  );
}

/**
 * Component that queries for analysis template data and displays the field table
 */
export default function QueryingAnalysisTemplateVariableFieldTable({
  analysisTemplateId,
  ownerId,
  ownerUrlId,
  registryId,
  onDraftFieldsChange,
  onAnalysisTemplateVariableMappingChange,
  onError,
  setIsLoading,
  schemaLookupData,
}: QueryingAnalysisTemplateVariableFieldTableProps): JSX.Element | null {
  const queryResult = useSkippableBenchlingQuery<GetAnalysisTemplate>(GET_ANALYSIS_TEMPLATE_QUERY, {
    variables: {analysisTemplateId},
    skip: !analysisTemplateId,
  });

  // Notify parent of loading state changes
  useEffect(() => {
    const isLoading =
      queryResult.type !== QueryResultType.SUCCESS && queryResult.type !== QueryResultType.SKIPPED;
    setIsLoading?.(isLoading);
  }, [queryResult.type, setIsLoading]);

  const {queriedTemplateFields, analysisTemplateVariableMapping, error} = useMemo(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      const analysisTemplate = queryResult.data.analysisTemplate;
      assertIsAccessible(analysisTemplate);

      const result = generateRunFieldsSpecFromAnalysisTemplateVariables(
        analysisTemplate as AnalysisTemplateGraphQL
      );

      if (!result.success) {
        return {
          queriedTemplateFields: [],
          analysisTemplateVariableMapping: {},
          error: result.error,
        };
      }

      const {fields, analysisVariableMapping} = convertFieldSpecsToClientFields(
        result.fields,
        schemaLookupData
      );

      return {
        queriedTemplateFields: fields,
        analysisTemplateVariableMapping: analysisVariableMapping,
        error: undefined,
      };
    }
    return {queriedTemplateFields: [], analysisTemplateVariableMapping: {}, error: undefined};
  }, [queryResult, schemaLookupData]);

  // Necessary to prevent circular useEffect triggers since onDraftFieldsChange is defined each render
  const mountedRef = useRef(true);

  // Clear error on mount to handle template switching
  useEffect(() => {
    onError?.(null);
    mountedRef.current = true;
  }, [onError]);

  useEffect(() => {
    if (
      (queryResult.type === QueryResultType.SUCCESS || queryResult.type === QueryResultType.SKIPPED) &&
      mountedRef.current
    ) {
      onDraftFieldsChange(queriedTemplateFields);
      onAnalysisTemplateVariableMappingChange?.(analysisTemplateVariableMapping);
      onError?.(error ?? null);
      mountedRef.current = false;
    }
  }, [
    queryResult.type,
    onDraftFieldsChange,
    onAnalysisTemplateVariableMappingChange,
    onError,
    queriedTemplateFields,
    analysisTemplateVariableMapping,
    error,
  ]);

  if (queryResult.type !== QueryResultType.SUCCESS && queryResult.type !== QueryResultType.SKIPPED) {
    return (
      <div>
        <h4>Automation template fields</h4>
        <CenteredHorizontalThrobber />
      </div>
    );
  }

  // Return null if there's an error - error will be displayed by parent component
  if (error) {
    return null;
  }

  // Only render if there are fields to display
  if (queriedTemplateFields.length === 0) {
    return null;
  }

  return (
    <div>
      <p className={css(styles.description)}>
        To save the automation template fields, please save the schema. Once saved, system names may be
        edited.
      </p>
      <AnalysisTemplateVariableFieldTable
        templateFields={queriedTemplateFields}
        ownerId={ownerId}
        ownerUrlId={ownerUrlId}
        registryId={registryId ?? schemaLookupData?.registryId}
        isPreview={true}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  description: {
    marginBottom: '8px',
    color: Color.GRAY_650,
    fontStyle: 'italic',
  },
});
